import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Lock, 
  LogOut, 
  Check, 
  X, 
  Trash2, 
  Image, 
  Video, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  BarChart3,
  TrendingUp,
  Users,
  Share2,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple bar chart component
const SimpleBarChart = ({ data, maxValue }) => {
  const colors = {
    twitter: '#1DA1F2',
    facebook: '#1877F2',
    whatsapp: '#25D366',
    linkedin: '#0A66C2',
    copy: '#6B7280'
  };

  return (
    <div className="space-y-3">
      {Object.entries(data).map(([platform, count]) => (
        <div key={platform} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="capitalize font-medium text-gray-700">
              {platform === 'twitter' ? 'X / Twitter' : platform}
            </span>
            <span className="text-gray-500">{count}</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${maxValue > 0 ? (count / maxValue) * 100 : 0}%`,
                backgroundColor: colors[platform] || '#009639'
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export const AdminPage = () => {
  const { t, language } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [posts, setPosts] = useState([]);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, total_shares: 0 });
  const [shareStats, setShareStats] = useState({ total_shares: 0, by_platform: {} });
  const [visitorCount, setVisitorCount] = useState(0);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);

    try {
      const formData = new FormData();
      formData.append('password', password);
      
      const response = await axios.post(`${API}/admin/login`, formData);
      
      if (response.data.success) {
        setIsLoggedIn(true);
        setAdminKey(password);
        toast.success('Login successful');
        fetchAllData(password);
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAdminKey('');
    setPassword('');
    setPosts([]);
    setPendingPosts([]);
  };

  const fetchAllData = async (key) => {
    setLoading(true);
    try {
      const [allRes, pendingRes, statsRes, shareStatsRes, visitorRes] = await Promise.all([
        axios.get(`${API}/admin/posts/all?admin_key=${key}`),
        axios.get(`${API}/admin/posts/pending?admin_key=${key}`),
        axios.get(`${API}/posts/stats`),
        axios.get(`${API}/shares/stats`),
        axios.get(`${API}/visitors/count`)
      ]);
      setPosts(allRes.data);
      setPendingPosts(pendingRes.data);
      setStats(statsRes.data);
      setShareStats(shareStatsRes.data);
      setVisitorCount(visitorRes.data.count);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (postId) => {
    setActionLoading(prev => ({ ...prev, [postId]: 'approve' }));
    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      
      await axios.post(`${API}/admin/posts/${postId}/approve`, formData);
      toast.success('Post approved');
      fetchAllData(adminKey);
    } catch (error) {
      toast.error('Failed to approve post');
    } finally {
      setActionLoading(prev => ({ ...prev, [postId]: null }));
    }
  };

  const handleReject = async (postId) => {
    setActionLoading(prev => ({ ...prev, [postId]: 'reject' }));
    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      
      await axios.post(`${API}/admin/posts/${postId}/reject`, formData);
      toast.success('Post rejected');
      fetchAllData(adminKey);
    } catch (error) {
      toast.error('Failed to reject post');
    } finally {
      setActionLoading(prev => ({ ...prev, [postId]: null }));
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    setActionLoading(prev => ({ ...prev, [postId]: 'delete' }));
    try {
      await axios.delete(`${API}/admin/posts/${postId}?admin_key=${adminKey}`);
      toast.success('Post deleted');
      fetchAllData(adminKey);
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setActionLoading(prev => ({ ...prev, [postId]: null }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  const PostCard = ({ post, showActions = true }) => (
    <Card className="admin-card overflow-hidden" data-testid={`admin-post-${post.id}`}>
      {post.media_url && (
        <div className="aspect-video bg-gray-100 relative">
          {post.media_type === 'image' ? (
            <img
              src={`${BACKEND_URL}${post.media_url}`}
              alt="Post media"
              className="w-full h-full object-cover"
            />
          ) : (
            <video
              src={`${BACKEND_URL}${post.media_url}`}
              className="w-full h-full object-cover"
              controls
            />
          )}
          <div className="absolute top-2 right-2">
            {post.media_type === 'image' ? (
              <Badge variant="secondary"><Image className="w-3 h-3 mr-1" /> Image</Badge>
            ) : (
              <Badge variant="secondary"><Video className="w-3 h-3 mr-1" /> Video</Badge>
            )}
          </div>
        </div>
      )}
      
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h4 className="font-semibold text-[#0a0a0a]">{post.name}</h4>
            <p className="text-sm text-gray-500">{post.email}</p>
          </div>
          {getStatusBadge(post.status)}
        </div>
        
        <div className="bg-gray-50 rounded-lg p-3 mb-3">
          <p className="text-sm text-[#009639] font-medium mb-1">The UAE is...</p>
          <p className="text-[#0a0a0a]">"{post.text}"</p>
        </div>
        
        <p className="text-xs text-gray-400 mb-4">{formatDate(post.created_at)}</p>
        
        {showActions && post.status === 'pending' && (
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => handleApprove(post.id)}
              disabled={actionLoading[post.id]}
              className="flex-1 bg-[#009639] hover:bg-[#007a2f]"
              data-testid={`approve-btn-${post.id}`}
            >
              {actionLoading[post.id] === 'approve' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><Check className="w-4 h-4 mr-1" /> {t('approve')}</>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleReject(post.id)}
              disabled={actionLoading[post.id]}
              className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
              data-testid={`reject-btn-${post.id}`}
            >
              {actionLoading[post.id] === 'reject' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><X className="w-4 h-4 mr-1" /> {t('reject')}</>
              )}
            </Button>
          </div>
        )}
        
        {showActions && (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => handleDelete(post.id)}
            disabled={actionLoading[post.id]}
            className="w-full mt-2 bg-red-500 hover:bg-red-600 text-white"
            data-testid={`delete-btn-${post.id}`}
          >
            {actionLoading[post.id] === 'delete' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <><Trash2 className="w-4 h-4 mr-1" /> {t('delete')}</>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  // Get max value for chart scaling
  const maxShareValue = Math.max(...Object.values(shareStats.by_platform || {}), 1);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      <Header />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {!isLoggedIn ? (
          <div className="max-w-md mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#009639]/10 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-[#009639]" />
                </div>
                <CardTitle className="text-2xl font-serif">{t('adminLogin')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <Input
                    type="password"
                    placeholder={t('passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-gray-50"
                    data-testid="admin-password-input"
                  />
                  <Button
                    type="submit"
                    disabled={loggingIn || !password}
                    className="w-full bg-[#009639] hover:bg-[#007a2f]"
                    data-testid="admin-login-btn"
                  >
                    {loggingIn ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {t('loginButton')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-serif font-bold text-[#0a0a0a]">{t('adminTitle')}</h1>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-gray-200"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
            </div>

            <Tabs defaultValue="analytics" className="space-y-6">
              <TabsList className="bg-white border shadow-sm">
                <TabsTrigger value="analytics" className="data-[state=active]:bg-[#009639] data-[state=active]:text-white">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="pending" className="data-[state=active]:bg-[#009639] data-[state=active]:text-white">
                  {t('pendingPosts')} ({pendingPosts.length})
                </TabsTrigger>
                <TabsTrigger value="all" className="data-[state=active]:bg-[#009639] data-[state=active]:text-white">
                  {t('allPosts')} ({posts.length})
                </TabsTrigger>
              </TabsList>

              {/* Analytics Dashboard */}
              <TabsContent value="analytics">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Total Visitors</p>
                          <p className="text-3xl font-bold text-[#0a0a0a]">{visitorCount}</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Total Messages</p>
                          <p className="text-3xl font-bold text-[#0a0a0a]">{stats.total}</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                          <MessageSquare className="w-6 h-6 text-green-600" />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="text-green-600">{stats.approved} approved</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-yellow-600">{stats.pending} pending</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Total Shares</p>
                          <p className="text-3xl font-bold text-[#0a0a0a]">{shareStats.total_shares}</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <Share2 className="w-6 h-6 text-purple-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Engagement Rate</p>
                          <p className="text-3xl font-bold text-[#0a0a0a]">
                            {visitorCount > 0 ? ((stats.total / visitorCount) * 100).toFixed(1) : 0}%
                          </p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-orange-600" />
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-400">Messages / Visitors</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Share Performance Chart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="border-0 shadow-md">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <Share2 className="w-5 h-5 text-[#009639]" />
                        Share Performance by Platform
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {Object.keys(shareStats.by_platform || {}).length > 0 ? (
                        <SimpleBarChart 
                          data={shareStats.by_platform} 
                          maxValue={maxShareValue}
                        />
                      ) : (
                        <div className="text-center py-8 text-gray-400">
                          <Share2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No shares yet</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-md">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#009639]" />
                        Quick Stats
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Approval Rate</span>
                          <span className="font-bold text-[#009639]">
                            {stats.total > 0 ? ((stats.approved / stats.total) * 100).toFixed(0) : 0}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Shares per Message</span>
                          <span className="font-bold text-purple-600">
                            {stats.approved > 0 ? (shareStats.total_shares / stats.approved).toFixed(1) : 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Pending Review</span>
                          <span className="font-bold text-yellow-600">{stats.pending}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Most Popular Platform</span>
                          <span className="font-bold text-blue-600 capitalize">
                            {Object.entries(shareStats.by_platform || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="pending">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                      <Card key={i} className="animate-pulse">
                        <div className="h-48 bg-gray-200" />
                        <CardContent className="p-4 space-y-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4" />
                          <div className="h-4 bg-gray-200 rounded w-1/2" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : pendingPosts.length === 0 ? (
                  <Card className="p-12 text-center">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-[#009639]" />
                    <p className="text-gray-500 text-lg">{t('noPending')}</p>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {pendingPosts.map(post => (
                      <PostCard key={post.id} post={post} />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="all">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                      <Card key={i} className="animate-pulse">
                        <div className="h-48 bg-gray-200" />
                        <CardContent className="p-4 space-y-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4" />
                          <div className="h-4 bg-gray-200 rounded w-1/2" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : posts.length === 0 ? (
                  <Card className="p-12 text-center">
                    <Eye className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 text-lg">No posts yet</p>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.map(post => (
                      <PostCard key={post.id} post={post} showActions={true} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
